import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { styles, Modal } from '../theme';

import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';

const emptyForm = { name: '', color: '#6366F1', is_cash: false, display_order: 0, is_active: 1 };

const PosPaymentMethods = ({ token }) => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [methods, setMethods] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchMethods(searchQuery); }, [searchQuery]);

  const fetchMethods = async (search = '') => {
    try {
      const queryParams = new URLSearchParams({ page: 1, per_page: 100, search });
      const res = await fetchWithAuth(`${API_URL}/pos/payment-methods?${queryParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setMethods(data.data);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  };

  const handleSubmit = async () => {
    if (!form.name) {
      alert('Missing required field (Name)');
      return;
    }

    try {
      const url = editingId ? `${API_URL}/pos/payment-methods/${editingId}` : `${API_URL}/pos/payment-methods`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        fetchMethods(searchQuery);
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm);
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error saving payment method:', err);
    }
  };

  const handleEdit = (m) => {
    setForm({
      name: m.name || '',
      color: m.color || '#6366F1',
      is_cash: !!m.is_cash,
      display_order: m.display_order || 0,
      is_active: m.is_active === undefined ? 1 : m.is_active
    });
    setEditingId(m.id);
    setShowForm(true);
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete payment method "${m.name}"?`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/payment-methods/${m.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        fetchMethods(searchQuery);
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error deleting payment method:', err);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>POS Payment Methods</h1>
        {canCreate('pos_payment_methods') && (
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} /> Add Payment Method
          </button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={`${editingId ? 'Edit' : 'Add'} Payment Method`}
        maxWidth="450px"
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Name *</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              style={{ width: '48px', height: '40px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
            />
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              style={{ ...styles.input, flex: 1 }}
            />
          </div>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={!!form.is_cash}
              onChange={(e) => setForm({ ...form, is_cash: e.target.checked })}
              style={{ marginRight: '8px' }}
            />
            Behaves like cash (prompts for amount tendered/change at checkout, counts toward shift cash reconciliation)
          </label>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Display Order</label>
          <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} style={styles.input} />
        </div>
        {editingId && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                style={{ marginRight: '8px' }}
              />
              Active
            </label>
          </div>
        )}
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Save</button>
        </div>
      </Modal>

      <div style={{ ...styles.card, marginBottom: '20px' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.input}
          />
        </div>
      </div>

      <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Color</th>
                <th style={styles.th}>Cash-like?</th>
                <th style={styles.th}>Order</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {methods.map(m => (
                <tr key={m.id}>
                  <td style={styles.td}>{m.name}</td>
                  <td style={styles.td}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '4px', backgroundColor: m.color }} />
                  </td>
                  <td style={styles.td}>{m.is_cash ? 'Yes' : 'No'}</td>
                  <td style={styles.td}>{m.display_order}</td>
                  <td style={styles.td}>
                    <span style={{ color: m.is_active ? '#059669' : '#b91c1c' }}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    {canUpdate('pos_payment_methods') && (
                      <button onClick={() => handleEdit(m)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                        <Edit size={18} />
                      </button>
                    )}
                    {canDelete('pos_payment_methods') && (
                      <button onClick={() => handleDelete(m)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                        <Trash2 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {methods.length === 0 && (
                <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af' }}>No payment methods yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PosPaymentMethods;
